import {
  _HTMLElement,
  initializeProps,
  fromAttributeValue,
  kebabToCamelCase,
} from '../utils';

/**
 * Manages changes to a component's inner and outer state by monitoring both
 * props and attributes and calling `updated` functions to let you know when
 * things have changed.
 *
 * [Inspired by SkateJS.](https://github.com/skatejs/skatejs/blob/5246f85abc166b8ffec3e87ce9c5775c425baf5a/packages/skatejs/src/with-update.js)
 *
 * NOTE: This mixin defines `attributeChangedCallback`. It is expected you don't
 * need to use it directly, as this mixin handles calling update for you, but if
 * you do don't forget to call super.
 *
 * ### Attribute types
 * This mixin uses `attributeTypes` to translate attribute values when accessing
 * them in `updated`. If a type isn't declared for an attribute, it defaults to
 * `string` (equivalent to how the native `getAttribute` method behaves).
 *
 * Supported types: `string`, `boolean` and `number`.
 *
 * For more detail on reflected attribute types, see [the spec](
 * https://html.spec.whatwg.org/multipage/common-dom-interfaces.html#reflecting-content-attributes-in-idl-attributes).
 *
 * @category mixin
 * @param {Class} [Base=HTMLElement] Optional base class to extend from.
 * @returns {Class}
 *
 * @example
 ```js
import { withUpdate } from '@updater-legacy/components/custom-element';

class MyCustomElement extends withUpdate() {
  // Define a static list of properties that `withUpdate` should manage.
  static props = ['value'];
  static observedAttributes = ['name', 'disabled'];
  static attributeTypes = {
    name: 'string' // Optional declaration; 'string' is the default type.
    disabled: 'boolean'
  };

  // The `_state` property is managed by default, intended for internal state.
  // We can set the initial state like this. It will trigger a call to `updated`.
  _state = {
    currentIndex: 0,
  }

  // Define an `updated` method to react to changes to managed properties.
  // This callback is invoked with the current values of all managed properties.
  updated({ _state: { currentIndex }, value, name }) {
    // Good place to render.
  }

  clickHandler(value) {
    // This mixin provides a convenience method for updating internal state.
    // It simply merges the new state object into the old state object
    // (assuming the internal state is stored as an object).
    this.mergeState({ currentIndex: this._state.currentIndex + 1 });
  }
}
 ```
 */
export const withUpdate = (Base = _HTMLElement) =>
  class WithUpdate extends Base {
    _pendingUpdate = null;

    constructor() {
      super();

      const attrs = this.constructor.observedAttributes || [];
      const props = ['_state'].concat(this.constructor.props || []);

      console.assert(
        !attrs.some((attr) => props.includes(attr)),
        `observedAttributes and props cannot overlap when using withUpdate(). They must include all unique keys
          props: ${props}
          attrs: ${attrs}
        `
      );

      this._props = initializeProps(props, () => undefined);
      this._attrs = initializeProps(attrs, () => null);

      // Store existing property values (if any) before we lose the chance to
      // do so by creating the getters/setters below.
      const initialProps = initializeProps(props, (prop) => this[prop]);

      // Create a getter/setter pair for each property name in `props`.
      // When set, we schedule a call to the `updated` callback, if defined.
      // In `updated`, we can react to all recent property changes at once.
      props.forEach((prop) => {
        Object.defineProperty(this, prop, {
          enumerable: true,
          get: () => this._props[prop],
          set: (newValue) => {
            if (newValue !== this[prop]) {
              this._scheduleUpdate();
              this._props[prop] = newValue;
            }
          },
        });

        // Apply "upgradeProperty" workaround, ensuring properties set before
        // the class is constructed work the same as if they were set after.
        // https://developers.google.com/web/fundamentals/web-components/best-practices#lazy-properties
        this[prop] = initialProps[prop];
      });
    }

    mergeState(value) {
      this._state = { ...this._state, ...value };
    }

    connectedCallback() {
      super.connectedCallback && super.connectedCallback();

      this._readyToUpdate = true;

      // For consistency and predictability, we don't allow `updated` to run
      // prior to `connected`.
      if (this._pendingUpdate) {
        // Run `updated` in a micro task instead of a macro task to allow
        // rendering as soon as possible on insertion into the DOM. We otherwise
        // use a macro task delay to allow collecting of updates into a batch,
        // but there's already an implied delay here (time until `connected`).
        // https://stackoverflow.com/a/25933985
        this._pendingUpdate((fn) => fn());
      }
    }

    attributeChangedCallback(name, oldVal, newVal) {
      super.attributeChangedCallback &&
        super.attributeChangedCallback(name, oldVal, newVal);

      if (newVal !== oldVal) {
        this._scheduleUpdate();
        this._attrs[name] = newVal;
      }
    }

    _scheduleUpdate() {
      if (!this._pendingUpdate && this.updated) {
        const oldValues = { ...this._props, ...this._attrsToProps() };

        new Promise((resolve) => {
          // Default to using a `setTimeout` delay, waiting until after the
          // current microtask queue has cleared, then trigger a call to
          // `updated`, keeping it from being called too frequently.
          this._pendingUpdate = (delayFn = setTimeout) =>
            delayFn(() => resolve());

          if (this._readyToUpdate) {
            this._pendingUpdate();
          }
        }).then(() => {
          this._pendingUpdate = null;
          this.updated({ ...this._props, ...this._attrsToProps() }, oldValues);
        });
      }
    }

    _attrsToProps() {
      return Object.keys(this._attrs).reduce((attrs, attr) => {
        const propName = kebabToCamelCase(attr);
        const attrType =
          (this.constructor.attributeTypes || {})[attr] || 'string';

        return {
          ...attrs,
          [propName]: fromAttributeValue(attrType)(this._attrs[attr]),
        };
      }, {});
    }
  };
